import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useState } from 'react';
import { Box, Button, Checkbox, Divider, Menu, MenuItem, Typography } from '@mui/material';
import { Icon } from '@livable/saas-react-components';
import useStyles from './subscriptionsFilterMenuStyles';
import { COUPON_PRODUCT_FILTER_ALL, COUPON_SCOPE, COUPON_STATUS } from './couponsModel';
const SCOPE_OPTIONS = [{
  value: COUPON_SCOPE.ACCOUNT,
  label: 'Account'
}, {
  value: COUPON_SCOPE.PROPERTY,
  label: 'Property'
}, {
  value: COUPON_SCOPE.BOTH,
  label: 'Both'
}];
const STATUS_OPTIONS = [{
  value: COUPON_STATUS.ACTIVE,
  label: 'Active'
}, {
  value: COUPON_STATUS.DRAFT,
  label: 'Draft'
}, {
  value: COUPON_STATUS.DEPLETED,
  label: 'Fully redeemed'
}, {
  value: COUPON_STATUS.EXPIRED,
  label: 'Expired'
}];

/** "All products" first: it is how an invoice-wide coupon shows up. */
function buildProductOptions(availableProducts) {
  return [{
    value: COUPON_PRODUCT_FILTER_ALL,
    label: COUPON_PRODUCT_FILTER_ALL
  }, ...(availableProducts !== null && availableProducts !== void 0 ? availableProducts : []).filter(product => product === null || product === void 0 ? void 0 : product.id).map(product => ({
    value: product.id,
    label: product.name || product.id
  }))];
}
export function CouponsFilterMenu(_ref) {
  var _filterScope$length, _filterStatus$length, _filterProducts$lengt;
  let filterScope = _ref.filterScope,
    filterStatus = _ref.filterStatus,
    filterProducts = _ref.filterProducts,
    _ref$availableProduct = _ref.availableProducts,
    availableProducts = _ref$availableProduct === void 0 ? [] : _ref$availableProduct,
    onScopeChange = _ref.onScopeChange,
    onStatusChange = _ref.onStatusChange,
    onProductsChange = _ref.onProductsChange,
    onApply = _ref.onApply;
  const classes = useStyles();
  const _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    anchorEl = _useState2[0],
    setAnchorEl = _useState2[1];
  const _useState3 = useState(filterScope || []),
    _useState4 = _slicedToArray(_useState3, 2),
    localScope = _useState4[0],
    setLocalScope = _useState4[1];
  const _useState5 = useState(filterStatus || []),
    _useState6 = _slicedToArray(_useState5, 2),
    localStatus = _useState6[0],
    setLocalStatus = _useState6[1];
  const _useState7 = useState(filterProducts || []),
    _useState8 = _slicedToArray(_useState7, 2),
    localProducts = _useState8[0],
    setLocalProducts = _useState8[1];
  const open = Boolean(anchorEl);
  const productOptions = buildProductOptions(availableProducts);
  const handleOpen = event => {
    setAnchorEl(event.currentTarget);
    setLocalScope(filterScope || []);
    setLocalStatus(filterStatus || []);
    setLocalProducts(filterProducts || []);
  };
  const handleClose = () => setAnchorEl(null);
  const createToggleHandler = setter => value => {
    setter(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
  };
  const toggleScope = createToggleHandler(setLocalScope);
  const toggleStatus = createToggleHandler(setLocalStatus);
  const toggleProducts = createToggleHandler(setLocalProducts);
  const handleApply = () => {
    onScopeChange(localScope);
    onStatusChange(localStatus);
    onProductsChange(localProducts);
    onApply === null || onApply === void 0 || onApply();
    handleClose();
  };
  const handleClear = () => {
    setLocalScope([]);
    setLocalStatus([]);
    setLocalProducts([]);
    onScopeChange([]);
    onStatusChange([]);
    onProductsChange([]);
    onApply === null || onApply === void 0 || onApply();
    handleClose();
  };
  const activeFilterCount = ((_filterScope$length = filterScope === null || filterScope === void 0 ? void 0 : filterScope.length) !== null && _filterScope$length !== void 0 ? _filterScope$length : 0) + ((_filterStatus$length = filterStatus === null || filterStatus === void 0 ? void 0 : filterStatus.length) !== null && _filterStatus$length !== void 0 ? _filterStatus$length : 0) + ((_filterProducts$lengt = filterProducts === null || filterProducts === void 0 ? void 0 : filterProducts.length) !== null && _filterProducts$lengt !== void 0 ? _filterProducts$lengt : 0);
  const renderSection = (label, options, localValue, toggle) => /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
    variant: "subtitle2",
    color: "text.secondary",
    className: classes.sectionLabel
  }, label), options.map(opt => /*#__PURE__*/React.createElement(MenuItem, {
    key: opt.value,
    onClick: () => toggle(opt.value),
    disableRipple: true,
    className: classes.menuItem
  }, /*#__PURE__*/React.createElement(Checkbox, {
    size: "small",
    checked: localValue.includes(opt.value),
    disableRipple: true
  }), opt.label)));
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    size: "small",
    className: classes.filterButton,
    "aria-controls": "coupons-filter-menu",
    "aria-haspopup": "true",
    "aria-expanded": open ? 'true' : undefined,
    onClick: handleOpen,
    startIcon: /*#__PURE__*/React.createElement(Icon, {
      type: "filter"
    })
  }, "Filters", activeFilterCount > 0 && /*#__PURE__*/React.createElement("span", {
    className: classes.filterBadge
  }, activeFilterCount)), /*#__PURE__*/React.createElement(Menu, {
    id: "coupons-filter-menu",
    anchorEl: anchorEl,
    open: open,
    onClose: handleClose,
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'right'
    },
    transformOrigin: {
      vertical: 'top',
      horizontal: 'right'
    },
    PaperProps: {
      className: classes.menuPaper
    },
    MenuListProps: {
      className: classes.menuList
    }
  }, /*#__PURE__*/React.createElement(Box, {
    className: classes.menuScrollContent
  }, renderSection('Scope', SCOPE_OPTIONS, localScope, toggleScope), /*#__PURE__*/React.createElement(Divider, {
    className: classes.divider
  }), renderSection('Status', STATUS_OPTIONS, localStatus, toggleStatus), /*#__PURE__*/React.createElement(Divider, {
    className: classes.divider
  }), renderSection('Product', productOptions, localProducts, toggleProducts)), /*#__PURE__*/React.createElement(Box, {
    className: classes.actionBar
  }, /*#__PURE__*/React.createElement(Button, {
    size: "small",
    onClick: handleClear
  }, "Clear"), /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    size: "small",
    onClick: handleApply
  }, "Apply"))));
}